import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { mrnForDisplay } from "@tailormed/common-client/util/mrn";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Typography, Table, Upload, Empty, Pagination, Input, Popconfirm, Space, message } from "antd";
import { InboxOutlined, CloseCircleFilled } from "@ant-design/icons";
import { OverflowTooltip } from "@tailormed/common-client/component/OverflowTooltip";
import styled from "styled-components";

import { useApi, ApiNames } from "../../hooks/api";
import { useGetSetting } from "../../hooks/getSetting";
import { selectIsPhiAllowed } from "../../store/selector";
import { WHITE, GRAY_25, GRAY_300, GRAY_500, GRAY_700, PRIMARY_600, WARNING_700 } from "../../constant/colors";
import { MB, separateFiles } from "../../component/Uploader";
import { TableWrap, ActionButton, SecondaryActionButton } from "./taskStyles";
import { patientLabel, formatRelativeTime } from "./taskHelpers";
import { useAutoRefetch } from "./useAutoRefetch";
import { SectionError } from "./SectionError";
import { ScreenshotReviewPanel, SectionChips } from "./ScreenshotReviewPanel";
import {
  canSubmitTask,
  emrSectionsFor,
  isDiscarded,
  isIdentifying,
  matchUploadResults,
  mergeViews,
  missingSectionLabelKeys,
  reviewedTaskIds,
  taskMissingSections,
  taskStatusKey
} from "./screenshotReview";

const { Text } = Typography;
const { Dragger } = Upload;

const PAGE_SIZE = 5;

// The review's patient picker + per-patient coverage read the whole open queue
// (small by nature — it is the patients waiting on an operator), not one page.
const ALL_TASKS_LIMIT = 200;

// While the agent is still identifying a screenshot, re-check this often —
// backing off (doubling, up to the max) while the status check keeps failing.
const IDENTIFY_POLL_MS = 3000;
const IDENTIFY_POLL_MAX_MS = 30000;

// Image MIME types the screenshot uploader accepts. Drives both the file-dialog
// `accept` hint and the staging guard — drag-drop and programmatic adds bypass
// `accept`, so the guard is the real enforcement point.
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

// Bulk limits, enforced at staging so the upload never dies on the backend:
// FN's skipper stream caps the whole multipart at ~15MB, so the per-file and
// per-bulk caps must fit under it.
const MAX_SCREENSHOTS_PER_BULK = 10;
const MAX_SCREENSHOT_MB = 5;
const MAX_BULK_TOTAL_MB = 14;

// Error/warning toasts carry the reason the user must act on — antd's 3s
// default vanishes before it can be read.
const WARNING_TOAST_SECONDS = 8;
const ERROR_TOAST_SECONDS = 10;

const UploadArea = styled.div`
  padding: 16px;
`;

const StagedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 14px;
  cursor: default;
`;

const ThumbnailCard = styled.div`
  position: relative;
  background: ${WHITE};
  border: 1px solid ${GRAY_300};
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100px;
  object-fit: contain;
  background: ${GRAY_25};
  border-radius: 4px;
`;

const ThumbnailName = styled(Text)`
  font-size: 11px;
  color: ${GRAY_700};
  width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: ${WHITE};
  border: none;
  border-radius: 50%;
  padding: 0;
  line-height: 0;
  cursor: pointer;
  font-size: 18px;
  color: ${GRAY_500};
  transition: color 120ms ease-in-out;
  &:hover {
    color: ${PRIMARY_600};
  }
`;

const StagingActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
`;

/**
 * The last non-null value seen. `useFetch` resets its result to null at the
 * start of every request, so a background refetch would otherwise blank the
 * table and the review's pickers for the length of the round trip.
 */
const useLastResult = (result) => {
  const lastRef = useRef(result);
  if (result != null) lastRef.current = result;
  return lastRef.current;
};

/**
 * Tasks hub — EMR screenshots section. UX flow:
 *
 *   1. Operator drops screenshots (any patients, any order) and clicks Upload.
 *      No patient picking — the agent identifies each screenshot's patient and
 *      which sections (medications or treatment plan / coverages) it shows, within seconds.
 *   2. The review panel shows the result grouped by patient: screenshots the
 *      agent couldn't attribute (with why, and an "assign to" picker), patients
 *      still missing a section, and a Submit per patient.
 *   3. Submit hands the patient's screenshots to the agent. A task whose
 *      screenshots turn out incomplete comes back to the table as "Needs
 *      more screenshots"; "Fill manually" is always available instead.
 *
 * Review state lives on the server (screenshot ids + their identification),
 * so unattributed uploads survive a reload via the `unresolved` feed.
 */
const ScreenshotTasksSection = ({ onCountChange, refreshNonce }) => {
  const { t } = useTranslation();

  const [fetchTasks, latestTasksResult, tasksLoading, tasksError] = useApi(ApiNames.AgenticEmrTasksList);
  const [fetchAllTasks, latestAllTasksResult] = useApi(ApiNames.AgenticEmrTasksList);
  const tasksResult = useLastResult(latestTasksResult);
  const allTasksResult = useLastResult(latestAllTasksResult);
  const [uploadScreenshots, , uploading] = useApi(ApiNames.AgenticScreenshotsUpload);
  const [fetchStatus] = useApi(ApiNames.AgenticEmrScreenshotsStatus);
  const [fetchUnresolved] = useApi(ApiNames.AgenticEmrScreenshotsUnresolved);
  const [assignScreenshot, , assigning] = useApi(ApiNames.AgenticEmrScreenshotAssign);
  const [discardScreenshot, , discarding] = useApi(ApiNames.AgenticEmrScreenshotDiscard);
  const [submitTasks, , submitting] = useApi(ApiNames.AgenticEmrTasksSubmit);
  const [chooseManualFill, , choosingManual] = useApi(ApiNames.AgenticEmrTaskManualFill);
  const isPhiAllowed = useSelector(selectIsPhiAllowed);
  // The site's required EMR sections: a site that reads drug activity off the
  // treatment plan needs the plan instead of the medication list.
  const [activityFromTreatmentPlan] = useGetSetting(["agenticEmrActivityFromTreatmentPlan"]);
  const sections = emrSectionsFor(activityFromTreatmentPlan);
  const busy = uploading || assigning || discarding || submitting || choosingManual;

  // ── Server-side pagination + filter ──────────────────────────────
  const [tablePage, setTablePage] = useState(1);
  const [tableFilter, setTableFilter] = useState("");

  // ── Staging (files picked, not uploaded yet) ──────────────────────
  const [stagedItems, setStagedItems] = useState([]);

  // ── Review (uploaded screenshots, keyed by screenshotId) ──────────
  const [reviewViews, setReviewViews] = useState(() => new Map());
  // screenshotId → local object URL of the operator's own file (this session).
  const [previews, setPreviews] = useState(() => new Map());

  // ── Stable refs ───────────────────────────────────────────────────
  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;
  const fetchAllTasksRef = useRef(fetchAllTasks);
  fetchAllTasksRef.current = fetchAllTasks;
  const fetchStatusRef = useRef(fetchStatus);
  fetchStatusRef.current = fetchStatus;
  const fetchUnresolvedRef = useRef(fetchUnresolved);
  fetchUnresolvedRef.current = fetchUnresolved;
  const tablePageRef = useRef(tablePage);
  tablePageRef.current = tablePage;
  const tableFilterRef = useRef(tableFilter);
  tableFilterRef.current = tableFilter;

  const refetch = useCallback(() => {
    const params = { limit: PAGE_SIZE, offset: (tablePageRef.current - 1) * PAGE_SIZE };
    const pid = parseInt(tableFilterRef.current, 10);
    if (!isNaN(pid) && pid > 0) params.patientId = pid;
    fetchTasksRef.current(params);
    fetchAllTasksRef.current({ limit: ALL_TASKS_LIMIT, offset: 0 });
  }, []);

  // Initial load + auto-refresh, paused while an operator action is in flight
  // so a poll can't swap the rows mid-action.
  useAutoRefetch(refetch, busy);

  // Re-fetch on page / filter change (skip initial mount)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    refetch();
  }, [tablePage, tableFilter, refetch]);

  // Manual refresh from hub header
  useEffect(() => {
    if (refreshNonce > 0) refetch();
  }, [refreshNonce, refetch]);

  // Uploads nobody could attribute survive a reload: pull them into the review.
  useEffect(() => {
    Promise.resolve(fetchUnresolvedRef.current())
      .then((result) => {
        if (result?.screenshots?.length) setReviewViews((prev) => mergeViews(prev, result.screenshots));
      })
      .catch(() => {
        /* the tray is a convenience — the table still works without it */
      });
  }, []);

  // Poll while the agent is still identifying any reviewed screenshot.
  const pendingIds = useMemo(
    () =>
      Array.from(reviewViews.values())
        .filter((view) => isIdentifying(view) && !isDiscarded(view))
        .map((view) => view.screenshotId),
    [reviewViews]
  );
  // Consecutive failed status checks. A failed check (`useApi` resolves
  // undefined, never throws) changes no view, so `pendingIds` alone would never
  // re-arm the poll — bumping this does, with a backoff.
  const [pollFailures, setPollFailures] = useState(0);
  useEffect(() => {
    if (pendingIds.length === 0) return undefined;
    const delay = Math.min(IDENTIFY_POLL_MS * 2 ** pollFailures, IDENTIFY_POLL_MAX_MS);
    const timer = setTimeout(async () => {
      const result = await fetchStatusRef.current({ ids: pendingIds });
      if (!result?.screenshots) {
        setPollFailures((failures) => failures + 1);
        return;
      }
      setPollFailures(0);
      // Only screenshots still in the review — one submitted or hidden while
      // this check was in flight stays gone.
      setReviewViews((prev) => mergeViews(prev, result.screenshots, { onlyExisting: true }));
      if (result.screenshots.some((view) => !isIdentifying(view))) refetch();
    }, delay);
    return () => clearTimeout(timer);
  }, [pendingIds, pollFailures, refetch]);

  // Cleanup object URLs on unmount
  const stagedItemsRef = useRef(stagedItems);
  stagedItemsRef.current = stagedItems;
  const previewsRef = useRef(previews);
  previewsRef.current = previews;
  useEffect(() => {
    return () => {
      [...stagedItemsRef.current.map((item) => item.previewUrl), ...previewsRef.current.values()].forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          /* no-op */
        }
      });
    };
  }, []);

  const pendingRows = tasksResult?.rows ?? [];
  const openTasks = useMemo(() => allTasksResult?.rows ?? [], [allTasksResult]);
  const tasksById = useMemo(() => new Map(openTasks.map((task) => [task.taskId, task])), [openTasks]);
  const reviewList = useMemo(() => Array.from(reviewViews.values()), [reviewViews]);
  const reviewedIds = useMemo(() => reviewedTaskIds(reviewList), [reviewList]);

  useEffect(() => {
    if (tasksResult) onCountChange(tasksResult.total ?? 0);
  }, [tasksResult, onCountChange]);

  // ── Staging handlers ──────────────────────────────────────────────
  const handleStageFiles = (file, fileList) => {
    if (file === fileList[fileList.length - 1]) {
      const images = fileList.filter((f) => ACCEPTED_IMAGE_TYPES.includes(f.type));
      if (images.length < fileList.length) {
        message.warning(
          t(
            "agentic_platform.emr_tasks.upload_non_image",
            "Only PNG or JPEG images can be uploaded — other files were skipped."
          ),
          WARNING_TOAST_SECONDS
        );
      }

      const { validFiles: sized } = separateFiles(images, MAX_SCREENSHOT_MB * MB);
      if (sized.length < images.length) {
        message.warning(
          t(
            "agentic_platform.emr_tasks.upload_too_large",
            "Some images are larger than {{max}}MB and were not added.",
            { max: MAX_SCREENSHOT_MB }
          ),
          WARNING_TOAST_SECONDS
        );
      }

      if (sized.length) {
        setStagedItems((prev) => {
          // Cap the bulk by COUNT and by TOTAL bytes (FN's upload stream cap)
          // — stage what fits, warn about the rest.
          let total = prev.reduce((sum, item) => sum + item.file.size, 0);
          const accepted = [];
          for (const f of sized) {
            if (prev.length + accepted.length >= MAX_SCREENSHOTS_PER_BULK) break;
            if (total + f.size > MAX_BULK_TOTAL_MB * MB) break;
            accepted.push(f);
            total += f.size;
          }
          if (accepted.length < sized.length) {
            message.warning(
              t(
                "agentic_platform.emr_tasks.upload_bulk_limit",
                "You can upload up to {{maxCount}} screenshots at a time. Upload these first, then add the rest.",
                { maxCount: MAX_SCREENSHOTS_PER_BULK }
              ),
              WARNING_TOAST_SECONDS
            );
          }
          const newItems = accepted.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }));
          return newItems.length ? [...prev, ...newItems] : prev;
        });
      }
    }
    return Upload.LIST_IGNORE;
  };

  const revoke = (url) => {
    try {
      URL.revokeObjectURL(url);
    } catch (e) {
      /* no-op */
    }
  };

  // A screenshot that left the review no longer needs its local preview.
  const dropPreviews = (screenshotIds) => {
    if (screenshotIds.length === 0) return;
    setPreviews((prev) => {
      const next = new Map(prev);
      screenshotIds.forEach((id) => {
        const url = next.get(id);
        if (url) revoke(url);
        next.delete(id);
      });
      return next;
    });
  };

  const removeStagedItem = (index) => {
    setStagedItems((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) revoke(removed.previewUrl);
      return next;
    });
  };

  const clearStaging = () => {
    stagedItems.forEach((item) => revoke(item.previewUrl));
    setStagedItems([]);
  };

  // ── Upload → review ───────────────────────────────────────────────
  const handleUpload = async () => {
    if (stagedItems.length === 0) return;
    const formData = new FormData();
    stagedItems.forEach((item) => formData.append("screenshots", item.file));

    try {
      const result = await uploadScreenshots({ formData });
      // `useApi` resolves undefined on a failed request (it never throws).
      if (!result) throw new Error("upload failed");
      const uploaded = result.uploaded ?? [];
      const errors = result?.errors ?? [];

      // Match the response back to the staged items (by file name) so each
      // uploaded file's local preview carries over to its screenshot, and
      // anything that failed stays staged for retry.
      const { views, previewsById, retryItems, droppedItems } = matchUploadResults(stagedItems, result);
      const nextPreviews = new Map(previews);
      previewsById.forEach((previewUrl, screenshotId) => nextPreviews.set(screenshotId, previewUrl));
      droppedItems.forEach((item) => revoke(item.previewUrl));

      setPreviews(nextPreviews);
      setReviewViews((prev) => mergeViews(prev, views));
      setStagedItems(retryItems);

      if (errors.length === 0) {
        message.success(
          t("agentic_platform.emr_tasks.upload_review_ok", "Uploaded {{count}} screenshot(s) — review them below.", {
            count: uploaded.length
          })
        );
      } else if (uploaded.length > 0) {
        message.warning(
          t(
            "agentic_platform.emr_tasks.upload_review_partial",
            "{{ok}} uploaded, {{fail}} couldn’t be uploaded ({{reason}}) — they are still staged so you can retry.",
            { ok: uploaded.length, fail: errors.length, reason: errors[0]?.reason ?? "unknown reason" }
          ),
          WARNING_TOAST_SECONDS
        );
      } else {
        message.error(
          t(
            "agentic_platform.emr_tasks.upload_all_failed",
            "Couldn’t upload your screenshots ({{reason}}). Please try again.",
            {
              reason: errors[0]?.reason ?? "no details"
            }
          ),
          ERROR_TOAST_SECONDS
        );
      }
      refetch();
    } catch (err) {
      message.error(
        t("agentic_platform.emr_tasks.upload_failed", "Couldn't upload your screenshots. Please try again."),
        ERROR_TOAST_SECONDS
      );
    }
  };

  // ── Review actions ────────────────────────────────────────────────
  // `useApi` resolves `undefined` when a request fails (it never throws), so an
  // empty answer IS the failure signal for every action below.
  const actionFailed = (key, fallback) => message.error(t(key, fallback), ERROR_TOAST_SECONDS);

  // Resolves whether the assign went through, so the picker can reset on failure.
  const handleAssign = async (screenshotId, taskId) => {
    const view = await assignScreenshot({ screenshotId, taskId });
    if (!view) {
      actionFailed("agentic_platform.emr_tasks.assign_failed", "Couldn't assign the screenshot. Please try again.");
      return false;
    }
    setReviewViews((prev) => mergeViews(prev, [view]));
    refetch();
    return true;
  };

  const handleDiscard = async (screenshotId) => {
    const view = await discardScreenshot(screenshotId);
    if (!view) {
      actionFailed("agentic_platform.emr_tasks.discard_failed", "Couldn't remove the screenshot. Please try again.");
      return;
    }
    setReviewViews((prev) => mergeViews(prev, [view]));
    dropPreviews([screenshotId]);
    refetch();
  };

  // Drop from the review every screenshot that matches `leaves`, with its preview.
  const removeFromReview = (leaves) => {
    const leaving = Array.from(reviewViews.values())
      .filter(leaves)
      .map((view) => view.screenshotId);
    setReviewViews((prev) => {
      const next = new Map(prev);
      leaving.forEach((id) => next.delete(id));
      return next;
    });
    dropPreviews(leaving);
  };

  const handleSubmit = async (taskIds) => {
    const result = await submitTasks({ taskIds });
    if (!result) {
      actionFailed("agentic_platform.emr_tasks.submit_failed", "Couldn't submit. Please try again.");
      return;
    }
    const submitted = new Set(result.submitted ?? []);
    // Submitted patients leave the review — their screenshots are the agent's now.
    removeFromReview((view) => view.taskId != null && submitted.has(view.taskId));
    const failedCount = result.failed?.length ?? 0;
    if (failedCount > 0) {
      message.warning(
        t(
          "agentic_platform.emr_tasks.submit_some_failed",
          "{{count}} patient(s) couldn't be submitted — please try again.",
          {
            count: failedCount
          }
        ),
        WARNING_TOAST_SECONDS
      );
    } else {
      message.success(
        t("agentic_platform.emr_tasks.submit_ok", "Submitted {{count}} patient(s). The agent takes it from here.", {
          count: submitted.size
        })
      );
    }
    refetch();
  };

  const handleManualFill = async (taskId) => {
    const result = await chooseManualFill(taskId);
    if (!result) {
      actionFailed(
        "agentic_platform.emr_tasks.manual_fill_failed",
        "Couldn't switch to manual fill. Please try again."
      );
      return;
    }
    // The patient is filled by hand now — their screenshots leave the review.
    removeFromReview((view) => view.taskId === taskId);
    message.success(
      t("agentic_platform.emr_tasks.manual_fill_ok", "Moved to Manual EMR fill — enter the patient's data there.")
    );
    refetch();
  };

  // "Hide submitted": screenshots whose task is no longer waiting (submitted,
  // filled manually — here or elsewhere) and removed ones. Patients not yet
  // submitted and the unattributed tray stay.
  const clearReview = () => {
    removeFromReview((view) => isDiscarded(view) || (view.taskId != null && !tasksById.has(view.taskId)));
  };

  const renderStatus = (row) => {
    const key = taskStatusKey(row);
    const missing = taskMissingSections(row, sections);
    const attention = key.startsWith("needs_");
    return (
      <Space direction="vertical" size={2} data-testid={`agentic-task-status-${row.taskId}`}>
        <Text style={{ fontSize: 13, color: attention ? WARNING_700 : GRAY_700 }}>
          {t(`agentic_platform.emr_tasks.task_status.${key}`, { count: row.screenshots?.screenshots ?? 0 })}
        </Text>
        {missing.length > 0 && (
          <Text style={{ fontSize: 12, color: WARNING_700 }} data-testid={`agentic-task-missing-${row.taskId}`}>
            {t("agentic_platform.emr_tasks.review_missing", {
              sections: missingSectionLabelKeys(missing)
                .map((key) => t(key))
                .join(", ")
            })}
          </Text>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: t("agentic_platform.emr_tasks.col_patient", "Patient"),
      key: "patient",
      render: (_v, row) => (
        <a className="who-link" href={`/patient/${row.patientId}`} target="_blank" rel="noreferrer">
          {patientLabel(row)}
        </a>
      )
    },
    ...(isPhiAllowed
      ? [
          {
            title: t("agentic_platform.emr_tasks.col_mrn", "MRN"),
            key: "mrn",
            render: (_v, row) => {
              // The clipboard feeds an EMR lookup, so it has to carry what the EMR knows -- the same
              // value the cell shows, never the prefix we minted. Resolved once so they cannot drift.
              const mrnDisplay = mrnForDisplay(row);
              return mrnDisplay ? (
                <Text
                  className="muted"
                  style={{ fontFamily: "monospace", fontSize: 12 }}
                  copyable={{
                    text: mrnDisplay,
                    tooltips: [
                      t("agentic_platform.emr_tasks.copy_mrn", "Copy MRN"),
                      t("agentic_platform.emr_tasks.copied_mrn", "Copied")
                    ]
                  }}
                >
                  {mrnDisplay}
                </Text>
              ) : (
                <span className="muted">—</span>
              );
            }
          }
        ]
      : []),
    {
      title: t("agentic_platform.emr_tasks.col_drugs", "Drugs to search"),
      key: "drugsToSearch",
      render: (_v, row) =>
        row.drugsToSearch?.length ? (
          <OverflowTooltip
            text={row.drugsToSearch.join(" / ")}
            maxChars={50}
            textProps={{
              className: "muted",
              "data-testid": `agentic-task-drugs-${row.taskId}`
            }}
          />
        ) : (
          <span className="muted">—</span>
        )
    },
    {
      title: t("agentic_platform.emr_tasks.col_status", "Status"),
      key: "status",
      render: (_v, row) => renderStatus(row)
    },
    {
      title: t("agentic_platform.emr_tasks.col_sections", "Captured"),
      key: "sections",
      render: (_v, row) =>
        row.screenshots?.screenshots > 0 ? (
          <SectionChips
            shownSections={row.screenshots.shownSections}
            sections={sections}
            testId={`agentic-task-sections-${row.taskId}`}
          />
        ) : (
          <span className="muted">—</span>
        )
    },
    {
      title: t("agentic_platform.emr_tasks.col_created", "Pending since"),
      key: "createdAt",
      width: 140,
      render: (_v, row) => <span className="muted">{formatRelativeTime(row.createdAt)}</span>
    },
    {
      title: "",
      key: "actions",
      width: 220,
      render: (_v, row) => (
        <Space size={6}>
          {canSubmitTask(row, reviewedIds) ? (
            <ActionButton
              size="small"
              onClick={() => handleSubmit([row.taskId])}
              disabled={busy}
              data-testid={`agentic-task-submit-${row.taskId}`}
            >
              {t("agentic_platform.emr_tasks.submit_task", "Submit")}
            </ActionButton>
          ) : (
            taskStatusKey(row).startsWith("needs_") && (
              <Text className="muted" style={{ fontSize: 12 }} data-testid={`agentic-task-submit-hint-${row.taskId}`}>
                {t("agentic_platform.emr_tasks.needs_new_screenshots_hint", "Add screenshots or fill manually")}
              </Text>
            )
          )}
          <Popconfirm
            title={t(
              "agentic_platform.emr_tasks.manual_fill_confirm",
              "Enter this patient's EMR data by hand instead of screenshots?"
            )}
            okText={t("agentic_platform.emr_tasks.manual_fill", "Fill manually")}
            cancelText={t("agentic_platform.emr_tasks.submit_cancel", "Cancel")}
            onConfirm={() => handleManualFill(row.taskId)}
            disabled={busy}
            placement="bottomRight"
            // The app zeroes popover horizontal padding (craco.config.js) — restore it.
            overlayInnerStyle={{ padding: "0px 12px" }}
            okButtonProps={{ size: "small" }}
            cancelButtonProps={{ size: "small" }}
          >
            <SecondaryActionButton size="small" disabled={busy} data-testid={`agentic-task-manual-${row.taskId}`}>
              {t("agentic_platform.emr_tasks.manual_fill", "Fill manually")}
            </SecondaryActionButton>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const hasStaged = stagedItems.length > 0;
  const serverTotal = tasksResult?.total ?? 0;

  return (
    <>
      <UploadArea>
        <Dragger
          name="screenshots"
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          multiple
          showUploadList={false}
          beforeUpload={handleStageFiles}
          disabled={uploading}
          data-testid="agentic-screenshot-dragger"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: PRIMARY_600 }} />
          </p>
          <p className="ant-upload-text">
            {t("agentic_platform.emr_tasks.upload_hint", "Drag screenshots here, or click to browse")}
          </p>
          <p className="ant-upload-hint" style={{ color: GRAY_500 }}>
            {t(
              "agentic_platform.emr_tasks.upload_subhint",
              "Screenshots for any of the patients below, in any order — we'll work out who each one belongs to."
            )}
          </p>
          <p className="ant-upload-hint" style={{ color: GRAY_500, marginTop: 4 }}>
            {t(
              "agentic_platform.emr_tasks.upload_limits_hint",
              "PNG or JPEG · up to {{maxCount}} screenshots per upload · {{maxMb}}MB each",
              { maxCount: MAX_SCREENSHOTS_PER_BULK, maxMb: MAX_SCREENSHOT_MB }
            )}
          </p>
          {hasStaged && (
            <StagedGrid onClick={(e) => e.stopPropagation()}>
              {stagedItems.map((item, i) => (
                <ThumbnailCard key={`${item.file.name}-${i}`}>
                  <RemoveButton
                    aria-label={t("agentic_platform.emr_tasks.remove_staged", "Remove")}
                    title={t("agentic_platform.emr_tasks.remove_staged", "Remove")}
                    onClick={() => removeStagedItem(i)}
                    disabled={uploading}
                  >
                    <CloseCircleFilled />
                  </RemoveButton>
                  <Thumbnail src={item.previewUrl} alt={item.file.name} />
                  <ThumbnailName title={item.file.name}>{item.file.name}</ThumbnailName>
                </ThumbnailCard>
              ))}
            </StagedGrid>
          )}
        </Dragger>

        {hasStaged && (
          <StagingActions>
            <SecondaryActionButton onClick={clearStaging} disabled={uploading}>
              {t("agentic_platform.emr_tasks.clear_staged", "Clear")}
            </SecondaryActionButton>
            <Text className="muted" style={{ fontSize: 12 }}>
              {t("agentic_platform.emr_tasks.staged_counter", "{{count}}/{{max}} staged", {
                count: stagedItems.length,
                max: MAX_SCREENSHOTS_PER_BULK
              })}
            </Text>
            <ActionButton onClick={handleUpload} loading={uploading} data-testid="agentic-screenshot-upload">
              {t("agentic_platform.emr_tasks.upload_button", "Upload {{count}} screenshot(s)", {
                count: stagedItems.length
              })}
            </ActionButton>
          </StagingActions>
        )}
      </UploadArea>

      <ScreenshotReviewPanel
        views={reviewList}
        previews={previews}
        tasksById={tasksById}
        openTasks={openTasks}
        tasksTruncated={(allTasksResult?.total ?? 0) > ALL_TASKS_LIMIT}
        busy={busy}
        onAssign={handleAssign}
        onDiscard={handleDiscard}
        onSubmit={handleSubmit}
        onClear={clearReview}
        sections={sections}
      />

      {/* Patient ID filter */}
      <div style={{ padding: "8px 16px 4px" }}>
        <Input
          placeholder={t("agentic_platform.emr_tasks.filter_placeholder", "Filter by patient ID")}
          value={tableFilter}
          onChange={(e) => {
            setTableFilter(e.target.value);
            setTablePage(1);
          }}
          allowClear
          style={{ maxWidth: 220 }}
        />
      </div>

      {tasksError && !tasksLoading ? (
        <SectionError onRetry={refetch} testId="agentic-tasks-error-screenshots" />
      ) : !tasksLoading && pendingRows.length === 0 ? (
        <div style={{ padding: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              tableFilter
                ? t("agentic_platform.emr_tasks.empty_filtered", "No pending tasks match patient ID {{id}}.", {
                    id: tableFilter
                  })
                : t(
                    "agentic_platform.emr_tasks.empty",
                    "No pending tasks. New tasks appear here when the agent needs EMR data for a patient."
                  )
            }
          />
        </div>
      ) : (
        <TableWrap>
          <Table
            size="small"
            rowKey="taskId"
            columns={columns}
            dataSource={pendingRows}
            loading={tasksLoading}
            pagination={false}
          />
          {serverTotal > PAGE_SIZE && (
            <div style={{ padding: "12px 16px", textAlign: "right" }}>
              <Pagination
                current={tablePage}
                pageSize={PAGE_SIZE}
                total={serverTotal}
                onChange={(page) => setTablePage(page)}
                showSizeChanger={false}
                size="small"
              />
            </div>
          )}
        </TableWrap>
      )}
    </>
  );
};

export { ScreenshotTasksSection };
